import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SocialLinks from '../components/SocialLinks';
import { getSortedPostsData } from '../data/posts';
import { useTheme } from '../context/ThemeContext';

function Home() {
  const posts = getSortedPostsData();
  const recentPosts = posts.slice(0, 10);
  const { isDark, toggleTheme, layoutMode } = useTheme();

  const getExcerpt = (content, maxLength = 200) => {
    if (!content) return '';
    const plainText = content
      .replace(/#{1,6}\s+/g, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/---/g, '')
      .trim();
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  const bgColor = isDark ? '#0F0F0F' : '#FAF9F6';
  const textColor = isDark ? 'text-white' : 'text-black';
  const textMuted = isDark ? 'text-white/60' : 'text-black/60';
  const textVerySubtle = isDark ? 'text-white/30' : 'text-black/30';
  const borderColor = isDark ? 'border-white/5' : 'border-black/5';
  const linkHover = isDark ? 'hover:text-white/80' : 'hover:text-black/80';

  const renderDefaultLayout = () => (
    <div className="space-y-20">
      {recentPosts.map((post, idx) => (
        <motion.article
          key={post.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 + idx * 0.05 }}
          className={`border-b ${borderColor} pb-12 last:border-0`}
        >
          <div className={`text-xs ${textVerySubtle} font-mono mb-5 tracking-wider`} style={{ fontFeatureSettings: '"onum" 1' }}>
            {(() => {
              const dateParts = post.date.split('-');
              if (dateParts.length === 3) {
                const [year, month, day] = dateParts;
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${year} ${monthNames[parseInt(month) - 1]} ${parseInt(day)}`;
              } else {
                const [year, month] = dateParts;
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${year} ${monthNames[parseInt(month) - 1]}`;
              }
            })()}
          </div>
          <Link to={`/blog/${post.id}`}>
            <h2 className={`text-3xl font-light mb-6 leading-[1.3] ${linkHover} transition-colors ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              {post.title}
            </h2>
          </Link>
          <p className={`${textMuted} text-base leading-[1.7] mb-6 max-w-[52ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            {getExcerpt(post.content)}
          </p>
          <Link 
            to={`/blog/${post.id}`}
            className={`${textMuted} ${linkHover} text-sm underline underline-offset-2 transition-colors inline-block`}
            style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}
          >
            Read more →
          </Link>
        </motion.article>
      ))}
    </div>
  );

  const renderMacOSLayout = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl overflow-hidden"
      style={{ 
        background: isDark ? '#1e1e1e' : '#ffffff',
        boxShadow: isDark 
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255,255,255,0.1)' 
          : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)'
      }}
    >
      {/* Window Title Bar */}
      <div 
        className="flex items-center gap-2 px-4 py-3"
        style={{ background: isDark ? '#2d2d2d' : '#f5f5f5', borderBottom: isDark ? '1px solid #3d3d3d' : '1px solid #e5e5e5' }}
      >
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
          <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
          <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
        </div>
        <div className={`flex-1 text-center text-xs ${isDark ? 'text-white/50' : 'text-black/50'}`} style={{ fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}>
          Essays
        </div>
        <div className="w-14"></div>
      </div>
      
      {/* Toolbar */}
      <div 
        className="flex items-center gap-4 px-4 py-2 text-xs"
        style={{ background: isDark ? '#252525' : '#fafafa', borderBottom: isDark ? '1px solid #3d3d3d' : '1px solid #e5e5e5' }}
      >
        <span className={isDark ? 'text-white/40' : 'text-black/40'}>◀ ▶</span>
        <span className={isDark ? 'text-white/60' : 'text-black/60'}>~/writings/essays</span>
        <span className={`ml-auto ${isDark ? 'text-white/40' : 'text-black/40'}`}>{recentPosts.length} items</span>
      </div>

      {/* File Grid */}
      <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {recentPosts.map((post, idx) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: idx * 0.05 }}
          >
            <Link 
              to={`/blog/${post.id}`}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-blue-500/10 transition-colors group"
            >
              {/* File Icon */}
              <div 
                className="w-16 h-20 rounded-lg flex items-end justify-center pb-2 relative overflow-hidden"
                style={{ 
                  background: isDark 
                    ? 'linear-gradient(135deg, #3b3b3b 0%, #2a2a2a 100%)' 
                    : 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <div className="absolute top-0 right-0 w-4 h-4" style={{ background: isDark ? '#4a4a4a' : '#e0e0e0', clipPath: 'polygon(100% 0, 0 100%, 100% 100%)' }}></div>
                <span className={`text-xs font-mono ${isDark ? 'text-white/40' : 'text-black/40'}`}>.md</span>
              </div>
              {/* File Name */}
              <span 
                className={`text-xs text-center leading-tight ${isDark ? 'text-white/80 group-hover:text-white' : 'text-black/80 group-hover:text-black'} transition-colors line-clamp-2`}
                style={{ fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
              >
                {post.title}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  const renderTerminalLayout = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="font-mono text-sm"
      style={{ color: '#00ff41' }}
    >
      {/* Terminal Window */}
      <div 
        className="rounded-lg overflow-hidden"
        style={{ 
          background: '#0a0a0a',
          border: '1px solid #1a1a1a',
          boxShadow: '0 0 40px rgba(0, 255, 65, 0.03)'
        }}
      >
        {/* Terminal Title Bar */}
        <div className="flex items-center justify-between px-4 py-2" style={{ background: '#111', borderBottom: '1px solid #1a1a1a' }}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f56' }}></div>
            <div className="w-3 h-3 rounded-full" style={{ background: '#ffbd2e' }}></div>
            <div className="w-3 h-3 rounded-full" style={{ background: '#27ca3f' }}></div>
          </div>
          <span className="text-xs" style={{ color: '#444' }}>keshav@writings ~ </span>
          <div className="w-12"></div>
        </div>

        {/* Terminal Content */}
        <div className="p-6 space-y-1">
          <div style={{ color: '#666' }}>$ ls -la ./essays</div>
          <div className="h-4"></div>
          <div className="flex gap-8 text-xs mb-4" style={{ color: '#444' }}>
            <span>total {recentPosts.length}</span>
          </div>
          
          {recentPosts.map((post, idx) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: idx * 0.1 }}
            >
              <Link 
                to={`/blog/${post.id}`}
                className="flex items-baseline gap-4 py-1 hover:bg-white/5 px-2 -mx-2 rounded transition-colors group"
              >
                <span style={{ color: '#444' }}>-rw-r--r--</span>
                <span style={{ color: '#444' }}>keshav</span>
                <span style={{ color: '#555' }} className="w-16 text-right">
                  {Math.floor(post.content.length / 100)}kb
                </span>
                <span style={{ color: '#555' }}>
                  {(() => {
                    const dateParts = post.date.split('-');
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return `${monthNames[parseInt(dateParts[1]) - 1]} ${dateParts[2] || '01'}`;
                  })()}
                </span>
                <span className="group-hover:text-white transition-colors" style={{ color: '#00ff41' }}>
                  {post.title.toLowerCase().replace(/\s+/g, '-')}.md
                </span>
              </Link>
            </motion.div>
          ))}

          <div className="h-6"></div>
          <div className="flex items-center">
            <span style={{ color: '#666' }}>$ </span>
            <span className="animate-pulse" style={{ color: '#00ff41' }}>▋</span>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div 
      className={`min-h-screen ${textColor} transition-colors duration-300`} 
      style={{ fontFamily: '"Noto Sans", -apple-system, sans-serif', backgroundColor: bgColor }}
    >
      <SEO />
      <div className={`mx-auto px-6 py-20 ${layoutMode === 'macos' ? 'max-w-4xl' : layoutMode === 'terminal' ? 'max-w-3xl' : 'max-w-[60ch]'}`}>
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-12"
        >
          <h1 className={`text-5xl font-light tracking-tight mb-4 ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            Keshav Rao
          </h1>
          <div className="space-y-0">
            <p className={`${textMuted} text-lg font-light leading-none m-0 p-0`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              building intelligent systems at{' '}
              <a 
                href="https://atrislabs.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`${isDark ? 'text-white/80' : 'text-black/80'} ${linkHover} underline underline-offset-2`}
              >
                Atris Labs
              </a>.
            </p>
            <div className="flex items-center gap-2 pt-3">
            {/* Circle 1 - Theme toggle */}
            <button
              onClick={toggleTheme}
              className="w-5 h-5 rounded-full overflow-hidden relative group"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ background: isDark ? '#fafaf9' : '#1c1917' }}
            >
              {/* Blob 1 */}
              <motion.div
                className="absolute w-4 h-4 rounded-full"
                style={{ background: isDark ? '#c2410c' : '#fdba74', filter: 'blur(4px)' }}
                animate={{
                  x: ['-10%', '50%', '20%', '40%', '-10%'],
                  y: ['-10%', '30%', '60%', '10%', '-10%'],
                  scale: [1, 1.3, 0.9, 1.2, 1],
                }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Blob 2 */}
              <motion.div
                className="absolute w-3 h-3 rounded-full"
                style={{ background: isDark ? '#ea580c' : '#fb923c', filter: 'blur(4px)' }}
                animate={{
                  x: ['50%', '0%', '40%', '20%', '50%'],
                  y: ['40%', '10%', '50%', '30%', '40%'],
                  scale: [0.9, 1.2, 1, 1.3, 0.9],
                }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
              />
            </button>
          </div>
          </div>
        </motion.header>

        <motion.section
          key={layoutMode}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="mb-32"
        >
          {layoutMode === 'default' && renderDefaultLayout()}
          {layoutMode === 'macos' && renderMacOSLayout()}
          {layoutMode === 'terminal' && renderTerminalLayout()}
        </motion.section>

        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className={`border-t ${borderColor} pt-16`}
        >
          <SocialLinks isDark={isDark} />
          <div className="mt-8">
            <a href="/llms.txt" className={`text-xs ${textVerySubtle} ${linkHover} underline inline-block transition-colors`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              hello
            </a>
          </div>
          <div className="mt-2">
            <Link to="/select" className={`text-xs ${textVerySubtle} ${linkHover} underline inline-block transition-colors`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              select
            </Link>
          </div>
        </motion.footer>
      </div>
    </div>
  );
}

export default Home;
